import axiosInstance from "./axiosInstance";
import { sanitizeResponseData, sanitizeErrorResponse } from './../config/sanitizationConfig';

interface IDeleteConfig {
	withBody?: boolean;
}

class ApiService {
	/**
	 * Sanitize axios error response data in-place before rethrowing.
	 * Ensures no unsanitized error content reaches UI components.
	 */
	private sanitizeAndThrow(err: unknown): never {
		if (err && typeof err === "object" && "response" in err) {
			const axiosError = err as { response?: { data?: unknown; status?: number } };
			if (axiosError.response?.data) {
				axiosError.response.data = sanitizeErrorResponse(axiosError.response.data);
			}
		}
		// Log only safe metadata — never log full error object or response body
		const status = err && typeof err === "object" && "response" in err
			? (err as { response?: { status?: number } }).response?.status
			: undefined;
		throw err;
	}

	async get<T>(url: string, params: Record<string, unknown> = {}): Promise<T> {
		try {
			const response = await axiosInstance.get<T>(url, { params });
			/**
			 * Note: 204 responses return { data: null, status: 204 } cast as T.
			 * Callers should handle this shape when expecting possible 204s.
			 */
			if (response.status === 204) {
				return {
					data: null,
					status: response.status,
				} as T;
			}

			return sanitizeResponseData(response.data, url);
		} catch (err) {
			this.sanitizeAndThrow(err);
		}
	}

	async post<T, D = Record<string, unknown>>(url: string, data: D, config: Record<string, unknown> = {}): Promise<T> {
		try {
			const response = await axiosInstance.post<T>(url, data, config);
			return sanitizeResponseData(response.data, url);
		} catch (err) {
			this.sanitizeAndThrow(err);
		}
	}

	async put<T, D = Record<string, unknown>>(url: string, data: D, params: Record<string, unknown> = {}): Promise<T> {
		try {
			const response = await axiosInstance.put<T>(url, data, { params });
			return sanitizeResponseData(response.data, url);
		} catch (err) {
			this.sanitizeAndThrow(err);
		}
	}


	async delete<T>(url: string, paramsOrData?: Record<string, unknown>, config?: IDeleteConfig): Promise<T> {
		try {
			const { withBody, ...restConfig } = config || {};
			const response = withBody
				? await axiosInstance.delete<T>(url, { data: paramsOrData, ...restConfig })
				: await axiosInstance.delete<T>(url, { params: paramsOrData || {} });

			return sanitizeResponseData(response.data, url);
		} catch (err) {
			this.sanitizeAndThrow(err);
		}
	}

	async patch<T, D = Record<string, unknown>>(url: string, data: D, config: Record<string, unknown> = {}): Promise<T> {
		try {
			const response = await axiosInstance.patch<T>(url, data, config);
			return sanitizeResponseData(response.data, url);
		} catch (err) {
			this.sanitizeAndThrow(err);
		}
	}

	async downloadFile(url: string, params: Record<string, unknown> = {}): Promise<Blob> {
		try {
			const response = await axiosInstance.get(url, { params, responseType: "blob" });
			return response.data;
		} catch (err) {
			this.sanitizeAndThrow(err);
		}
	}
}

export const apiService = new ApiService();